/**
 * ZELLE, THE FIRST SCREEN OF THE WAYS TO PAY on an invoice's own page, in the owner's order (ops
 * #46): Zelle, then the bank, then the card, one screen at a time. No artboard draws it (business
 * issue 13), so it is built from drawn parts: Payments' white card and buttons
 * (`faithfully-kept-business/design/home/Payments.dc.html`), the heading of "How did we do?" above
 * it, and the seam's words (ops's `docs/seams/home.md` at 0b51e49, section 5), with what ops pushes
 * on the invoice (`pay.zelle`, section 3) and nothing home holds of its own. The words are in
 * `words.ts`, and the amount in every sentence is what is still to pay as ops's `formatCents` writes
 * it, "$212.00".
 *
 * - PAYING ASKS OPS NOTHING. Paying by Zelle happens in her bank, so the screen asks the office for
 *   nothing to pay; only her report that she sent it goes up, below, and the invoice reads paid only
 *   once ops pushes it (CLAUDE.md binding rule 1).
 * - THE TAG FIRST, WITH COPY (`ZelleTag`). On a phone the tag leads, in a card under the seam's
 *   sentence, with Copy, which puts it on her clipboard and says so in a live region. Where the
 *   clipboard refuses, the tag is selected for her to copy by hand, and where the browser has none,
 *   the tag alone is shown, selectable with one tap. Under them, in the card, the line ops's own
 *   page says under the tag, "Open your bank's app, choose Zelle, and paste <tag>."
 *   (`ZELLE.pasteInYourBank`, NEW), so a phone, which shows no code, says where the tag goes.
 * - THE TAG AND THE MEMO STAY WHOLE wherever a line holds them, in the sentence and in the steps,
 *   because she types them exactly: the tag moves to the next line as one piece rather than break
 *   at a hyphen of its own, and the memo stays on one line. The words are still the seam's, word
 *   for word; only where the line may break changes (`keptWhole`).
 * - THE CODE ON A WIDER SCREEN ONLY, beside the tag, drawn from `qr_url`, the address the code
 *   encodes, as the seam says it is (section 5 at 0b51e49, Q4, `ZelleCode.tsx`): she scans it with
 *   the phone in her hand, which she cannot do from the phone's own screen. "Open Zelle's page" is a
 *   plain link to that same address, opened in a new tab so this page stays where she left it
 *   (`rel="noopener noreferrer"`), on any screen, drawn as a text link (`TEXT_LINK`) as ops's own
 *   page draws it: on the owner's walk of 2026-10-16 it was the screen's biggest button, and on a
 *   phone it leads to a list of banks.
 * - "HOW DO I USE ZELLE?" folds away the seam's four steps (`zelleSteps`), each filled from what ops
 *   pushes, and then "Stuck?" with the office's number. The second step's "or scan the code" is drawn
 *   where the code is, on a wider screen, so a phone is not told to scan a code it does not show.
 * - "I SENT IT BY ZELLE" (slice V2, after the walk was replayed; reported to ops since round five's
 *   slice B2, ops's section 11.7, P53), in the card with the tag, under the line that says where to
 *   paste it, and under the line that the office matches Zelle payments by hand and the invoice
 *   will show as paid once it does (`ZELLE.matchedByHand`, NEW), which describes it. A text link,
 *   never a box: the replay found it drawn as the same box as "Pay another way" right under it, where
 *   a thumb aimed at one landed on the other. Pressed, it opens a step in its place (`SentStep`) that
 *   names exactly what she was asked to send, the amount, the tag and the memo, and asks the day she
 *   sent it, from the days the contract allows (`ReportOffer.days`), today first and chosen; "Yes, I
 *   sent it" hands the caller the day (`onReport`), which reports it, and "Not yet" closes it and
 *   gives the link its focus back. The focus goes to the step's question as it opens. While a report
 *   is out another press does nothing (`sending`), what did not go through is said under the step's
 *   buttons (`problem`), and a refusal is said where the link was (`refusal`), with no link while it
 *   stands. Drawn only where the caller gives `report` with a day to pick, which an invoice's own
 *   ways to pay, and its Zelle while she pays it by Zelle in place of her saved method, do.
 * - "PAY ANOTHER WAY" is offered only where the caller gives the next screen, which an invoice's page
 *   does only while ops says the bank and card screens exist (`pay.online`). Without it the seam's
 *   line gives the office's number in its place, since the ways to pay are offered nowhere else.
 * - `headingRef` is the heading, which the caller focuses when she comes back to Zelle from the
 *   screen after it; it never takes the focus when the page is first drawn.
 *
 * An invoice her saved method pays draws the same screen, without "Pay another way", once ops says
 * she is paying it by Zelle (l.730-740, `SavedMethodPays.tsx`), "I sent it by Zelle" among it, as
 * ops's section 11.7 says.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import type { Zelle } from '../api.ts';
import { dayInWords } from '../lib/dates.ts';
import {
  didYouSend,
  formatCents,
  sentOnChoice,
  ZELLE,
  zelleCostsNothing,
  zelleSteps,
} from '../words.ts';
import { CARD_BUTTON, GHOST_BUTTON, NAVY_BUTTON, TEXT_LINK } from './buttons.ts';
import { Chevron } from './Chevron.tsx';
import { Phone } from './Phone.tsx';
import { drawZelleCode } from './ZelleCode.tsx';

/**
 * The tag, which she pastes or types exactly, kept whole: it moves to the next line as one piece
 * rather than break at a hyphen of its own, where it would read as hyphenated, and breaks anywhere
 * only where it is longer than the whole line.
 */
export const TAG_WHOLE = 'inline-block max-w-full wrap-anywhere';

/** The memo, a few short words she types exactly, kept on one line. */
export const MEMO_WHOLE = 'whitespace-nowrap';

/** A piece of a line to draw whole, with the class that keeps it so. */
interface Whole {
  readonly words: string;
  readonly className: string;
}

/**
 * Draws a line of the seam's, word for word, with each piece given kept whole in its own span. The
 * pieces come in the order the line has them, and each is found from the end of what is left of
 * the line, so a word the line also holds earlier is never the one taken. Where a piece is not in
 * it, the line is drawn as it is, all of it plain, rather than any word moved.
 */
export function keptWhole(line: string, pieces: readonly Whole[]): ReactNode {
  const drawn: ReactNode[] = [];
  let rest = line;
  for (let index = pieces.length - 1; index >= 0; index -= 1) {
    const { words, className } = pieces[index]!;
    const at = words === '' ? -1 : rest.lastIndexOf(words);
    if (at === -1) return line;
    drawn.unshift(
      <span key={index} className={className}>
        {words}
      </span>,
      rest.slice(at + words.length),
    );
    rest = rest.slice(0, at);
  }
  return [rest, ...drawn];
}

/**
 * THE TAG, WITH COPY: the tag in the brand's serif, selectable with one tap, and Copy under it where
 * the browser has a clipboard, which says so in a live region once the tag is on it, and selects
 * the tag for her to copy by hand where the clipboard refuses. Then whatever the card says under it.
 * Zelle's screen draws it, and so does what she was asked to send once she says she sent it
 * (`ZelleSent.tsx`).
 */
export function ZelleTag({
  tag,
  children,
}: {
  readonly tag: string;
  readonly children?: ReactNode;
}) {
  const tagId = useId();
  const tagShown = useRef<HTMLParagraphElement>(null);
  /** Counts the copies made, so each one is said again in the live region. */
  const [copied, setCopied] = useState(0);
  const canCopy = typeof navigator.clipboard?.writeText === 'function';

  async function copy() {
    try {
      await navigator.clipboard.writeText(tag);
      setCopied((count) => count + 1);
    } catch {
      setCopied(0);
      const shown = tagShown.current;
      const selection = window.getSelection();
      if (shown === null || selection === null) return;
      const range = document.createRange();
      range.selectNodeContents(shown);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  return (
    <>
      <p
        id={tagId}
        ref={tagShown}
        className="font-serif text-h3 font-semibold wrap-anywhere text-text-brand select-all"
      >
        {tag}
      </p>
      {canCopy ? (
        <>
          <button
            type="button"
            aria-describedby={tagId}
            onClick={() => void copy()}
            className={`mt-3 cursor-pointer ${CARD_BUTTON}`}
          >
            {ZELLE.copy}
          </button>
          <p aria-live="polite" className="text-small text-text-muted">
            {copied > 0 ? (
              <span key={copied} className="mt-2 block">
                {ZELLE.copied}
              </span>
            ) : null}
          </p>
        </>
      ) : null}
      {children}
    </>
  );
}

/**
 * What "I sent it by Zelle" needs from its caller (ops's section 11.7): the days she may say she
 * sent it on, and what to do with her yes.
 */
export interface ReportOffer {
  /**
   * The days she may pick, `YYYY-MM-DD`, newest first, so the first is the one chosen
   * (`zelleSentDays`). With none, no link is drawn: there is no day ops would take.
   */
  readonly days: readonly string[];
  /** The place's own today, which the first day is called by when it is offered. */
  readonly today: string;
  /** Reports it, with the day she picked. */
  readonly onReport: (sentOn: string) => void;
  /** Her report is on its way to the request door, so another press does nothing. */
  readonly sending: boolean;
  /** What says her report did not go through, under the step's buttons, or nothing. */
  readonly problem?: ReactNode;
  /** What says her report was refused, in place of the link, or nothing. */
  readonly refusal?: ReactNode;
  /** The line that says the refusal, which takes the focus where it came of her press. */
  readonly refusalRef?: Ref<HTMLParagraphElement>;
}

interface ZelleWayProps {
  /** What is still to pay, as ops reads it. */
  readonly amountCents: number;
  readonly zelle: Zelle;
  /** Goes to the next way to pay; absent where there is none to go to. */
  readonly onAnotherWay?: () => void;
  /** "I sent it by Zelle", where the caller offers it. */
  readonly report?: ReportOffer;
  readonly headingRef?: Ref<HTMLHeadingElement>;
}

export function ZelleWay({ amountCents, zelle, onAnotherWay, report, headingRef }: ZelleWayProps) {
  const headingId = useId();
  const stepsId = useId();
  const [stepsOpen, setStepsOpen] = useState(false);
  const { tag, qrUrl, memo } = zelle;
  const amount = formatCents(amountCents);
  const steps = zelleSteps(zelle, amountCents);
  const code = useMemo(() => (qrUrl === undefined ? null : drawZelleCode(qrUrl)), [qrUrl]);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <div>
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="scroll-mt-4 font-serif text-h3 font-semibold text-pretty text-text-body"
        >
          {ZELLE.heading}
        </h2>
        <p className="mt-2 text-body text-pretty wrap-break-word">
          {keptWhole(zelleCostsNothing(amount, tag, memo), [
            { words: memo === undefined ? `${tag}.` : `${tag},`, className: TAG_WHOLE },
            ...(memo === undefined ? [] : [{ words: `${memo}.`, className: MEMO_WHOLE }]),
          ])}
        </p>
        <p className="mt-2 text-small text-pretty text-text-muted">{ZELLE.samePrice}</p>
      </div>

      <div className="rounded-card border border-border-subtle bg-surface-raised p-5 md:flex md:items-center md:gap-8">
        <div className="min-w-0 md:flex-1">
          <ZelleTag tag={tag}>
            <p className="mt-3 text-small text-pretty text-text-body wrap-break-word">
              {keptWhole(ZELLE.pasteInYourBank(tag), [{ words: `${tag}.`, className: TAG_WHOLE }])}
            </p>
            {report === undefined ? null : (
              <SentIt amount={formatCents(amountCents)} zelle={zelle} report={report} />
            )}
          </ZelleTag>
        </div>
        {code === null ? null : (
          <figure className="hidden shrink-0 md:flex md:flex-col md:items-center md:gap-2">
            {code}
            <figcaption className="max-w-50 text-center text-small text-pretty text-text-muted">
              {ZELLE.scan}
            </figcaption>
          </figure>
        )}
      </div>

      {qrUrl === undefined ? null : (
        <a
          href={qrUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`self-start text-body ${TEXT_LINK}`}
        >
          {ZELLE.openPage}
        </a>
      )}

      <div>
        <button
          type="button"
          aria-expanded={stepsOpen}
          aria-controls={stepsId}
          onClick={() => setStepsOpen((open) => !open)}
          className={`cursor-pointer gap-2 ${TEXT_LINK}`}
        >
          {ZELLE.howTo}
          <Chevron open={stepsOpen} />
        </button>
        {stepsOpen ? (
          <div id={stepsId} className="mt-2">
            <ol className="flex list-decimal flex-col gap-2 pl-6 text-body text-pretty wrap-break-word">
              {steps.map((step, index) =>
                index === 1 ? (
                  <li key={index}>
                    {ZELLE.pasteBeforeTag} <span className={TAG_WHOLE}>{tag}</span>
                    {code === null ? null : (
                      <span className="hidden md:inline">{ZELLE.orScan}</span>
                    )}
                    .
                  </li>
                ) : index === steps.length - 1 && memo !== undefined ? (
                  // The last step is sendTheAmount's, wherever the name ops may not send leaves it.
                  <li key={index}>
                    {keptWhole(step, [{ words: `${memo}.`, className: MEMO_WHOLE }])}
                  </li>
                ) : (
                  <li key={index}>{step}</li>
                ),
              )}
            </ol>
            <p className="mt-3 text-body text-pretty">
              {ZELLE.stuckBeforePhone} <Phone after="." />
            </p>
          </div>
        ) : null}
      </div>

      {onAnotherWay === undefined ? (
        <p className="text-body text-pretty">
          {ZELLE.cantUseBeforePhone} <Phone /> {ZELLE.cantUseAfterPhone}
        </p>
      ) : (
        <button type="button" onClick={onAnotherWay} className={`cursor-pointer ${GHOST_BUTTON}`}>
          {ZELLE.anotherWay}
        </button>
      )}
    </section>
  );
}

/**
 * "I SENT IT BY ZELLE" in the card with the tag: the line that the office matches Zelle payments by
 * hand, which describes it, then the link, the step it opens in its place, or what refused her
 * report in its place, said in a live region.
 */
function SentIt({
  amount,
  zelle,
  report,
}: {
  readonly amount: string;
  readonly zelle: Zelle;
  readonly report: ReportOffer;
}) {
  const matchedId = useId();
  const link = useRef<HTMLButtonElement>(null);
  const [asking, setAsking] = useState(false);
  /** "Not yet" closed the step, so the link it took the place of takes the focus back. */
  const closed = useRef(false);
  const refused = report.refusal !== undefined && report.refusal !== null;
  const offered = !refused && report.days.length > 0;

  useEffect(() => {
    if (refused) setAsking(false);
  }, [refused]);
  useEffect(() => {
    if (asking || !closed.current) return;
    closed.current = false;
    link.current?.focus();
  }, [asking]);

  return (
    <div className="mt-4 border-t border-border-subtle pt-3">
      <p id={matchedId} className="text-small text-pretty text-text-body">
        {ZELLE.matchedByHand}
      </p>
      {offered && asking ? (
        <SentStep
          amount={amount}
          zelle={zelle}
          report={report}
          onNotYet={() => {
            closed.current = true;
            setAsking(false);
          }}
        />
      ) : offered ? (
        <button
          ref={link}
          type="button"
          aria-describedby={matchedId}
          onClick={() => setAsking(true)}
          className={`cursor-pointer text-body font-semibold ${TEXT_LINK}`}
        >
          {ZELLE.sentIt}
        </button>
      ) : null}
      <div aria-live="polite">
        {refused ? (
          <p ref={report.refusalRef} tabIndex={-1} className="mt-2 text-body text-pretty">
            {report.refusal}
          </p>
        ) : offered && asking ? null : (
          (report.problem ?? null)
        )}
      </div>
    </div>
  );
}

/**
 * THE STEP "I SENT IT BY ZELLE" OPENS (round five, slice B2; ops's section 11.7): a group named by
 * its question, which names exactly what she was asked to send, the amount, the tag and the memo,
 * each of the last two kept whole on its line; the day she sent it, picked from the days the
 * contract allows, today first and chosen; "Yes, I sent it", the navy button, which hands the
 * caller the day; and "Not yet", a text link, which closes it. The question takes the focus as it
 * opens, so a screen reader says what she is being asked. What did not go through is said under the
 * buttons, in a live region.
 */
function SentStep({
  amount,
  zelle,
  report,
  onNotYet,
}: {
  readonly amount: string;
  readonly zelle: Zelle;
  readonly report: ReportOffer;
  readonly onNotYet: () => void;
}) {
  const questionId = useId();
  const pickerId = useId();
  const question = useRef<HTMLHeadingElement>(null);
  const [picked, setPicked] = useState<string | null>(null);
  // The day she picked while it is still offered, else the first, today where today is offered.
  const day = picked !== null && report.days.includes(picked) ? picked : report.days[0]!;
  const { tag, memo } = zelle;

  useEffect(() => {
    question.current?.focus();
  }, []);

  return (
    <div role="group" aria-labelledby={questionId} className="mt-2 flex flex-col gap-3">
      <h3
        id={questionId}
        ref={question}
        tabIndex={-1}
        className="text-body font-semibold text-pretty wrap-break-word text-text-body"
      >
        {keptWhole(didYouSend(amount, tag, memo), [
          { words: tag, className: TAG_WHOLE },
          ...(memo === undefined ? [] : [{ words: `${memo}?`, className: MEMO_WHOLE }]),
        ])}
      </h3>
      <div className="flex flex-col gap-1">
        <label htmlFor={pickerId} className="text-small text-text-body">
          {ZELLE.sentOnLabel}
        </label>
        <select
          id={pickerId}
          value={day}
          onChange={(event) => setPicked(event.target.value)}
          className="min-h-tap w-full cursor-pointer rounded-control border-2 border-border-strong bg-surface-raised px-3 text-body text-text-body"
        >
          {report.days.map((each) => (
            <option key={each} value={each}>
              {sentOnChoice(dayInWords(each) ?? each, each === report.today)}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        aria-disabled={report.sending ? true : undefined}
        onClick={() => {
          if (!report.sending) report.onReport(day);
        }}
        className={`cursor-pointer aria-disabled:cursor-wait aria-disabled:opacity-70 ${NAVY_BUTTON}`}
      >
        {ZELLE.yesSent}
      </button>
      <button
        type="button"
        onClick={onNotYet}
        className={`self-start cursor-pointer text-body ${TEXT_LINK}`}
      >
        {ZELLE.notYet}
      </button>
      <div aria-live="polite">{report.problem ?? null}</div>
    </div>
  );
}
